import { useState } from "react";
import { Box, Switch, Typography } from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import BoltIcon from "@mui/icons-material/Bolt";
import { useDataProvider, useNotify, useRecordContext, useRefresh, useUpdate } from "react-admin";
import { ConfirmationDialog } from "@/components/ui/ConfirmationDialog";
import { ResourceName, type Policy } from "@/types";
import { ConfirmLabelType } from "@/types/shared/uiComponents";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { useActivePartner } from "@/hooks/useActivePartner";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";

/**
 * Accel/Prompt-Pay funding eligibility for the policy — the signal that decides
 * whether a claim on this policy offers the Aura funding track at all
 * (ClaimsTab's two-track split). It renders as a full-width banner at the top of
 * the Overview section rather than as a cell in the field grid, for two reasons:
 *
 *  - `EditableGrid` treats falsy as empty, and the column is NOT NULL DEFAULT
 *    FALSE with backfill deferred. As a grid field the flag was therefore
 *    unrendered on every policy that hasn't been backfilled — hidden in exactly
 *    the state a user most needs to confirm. A banner is immune to that rule.
 *  - Width alone doesn't convey weight. A full-width grid row reads as a wider
 *    row; contrast is what makes a consequential flag legible at a glance.
 *
 * Both states are always rendered and differ by colour weight — an
 * incorrectly-disabled policy has to be as noticeable as an incorrectly-enabled
 * one, so "off" is a muted banner, never an absent one.
 *
 * Every change confirms first: flipping this changes whether claims on the
 * policy can be funded through Aura, which moves real money. Enabling is an
 * explicit attestation that a signed funding contract exists — the audit-log
 * entry on the field change is the record of who attested and when. The dialog
 * also states that Aegle confirms the contract in house before the first
 * funding request is approved, so the attestation is not the only control.
 *
 * Edit affordance is gated on policies edit permission AND a writable partner;
 * the server-side guard remains the enforcement of record.
 */
export const AccelFundingBanner = () => {
  const policy = useRecordContext<Policy>();
  const { canEdit } = useResourcePermissions(ResourceName.POLICIES);
  const { readOnly } = useActivePartner();
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();
  const [update] = useUpdate();
  const [busy, setBusy] = useState(false);
  // The value awaiting confirmation (null = no dialog open).
  const [pending, setPending] = useState<boolean | null>(null);

  if (!policy) return null;

  const enabled = !!policy.is_accel_funding_enabled;
  const editable = canEdit && !readOnly;
  // Read palette through sx callbacks so the banner follows dark mode and
  // partner branding without pulling in the branding-fetching useAppTheme hook.
  const accent = (theme: Theme) => (enabled ? theme.palette.success.main : theme.palette.text.disabled);

  // Policies are written through PUT, and the backend PUT handler zeroes any
  // column absent from the payload (see dataProvider.update). So the whole record
  // goes back with the one field changed — the same thing react-admin's
  // InPlaceEditor does for every inline edit on this page. A sparse write here is
  // NOT an option: PATCH deliberately omits is_accel_funding_enabled from its
  // surface (mgmt-api handlers/policies.go), so it would be silently dropped.
  //
  // Re-read the record immediately before writing rather than spreading the
  // render-time useRecordContext snapshot (!4490 review). A full-record PUT built
  // from a stale snapshot silently reverts any column another user or tab changed
  // in the meantime, and unlike an inline editor — open for the seconds between
  // clicking edit and saving — this banner is armed for as long as the page is
  // open, so that snapshot can be arbitrarily old. This narrows the lost-update
  // window to a single round trip. It does not eliminate it: closing it properly
  // needs optimistic concurrency (a version/ETag the API rejects on mismatch),
  // which the policy endpoints don't offer today.
  const setEnabled = async (value: boolean) => {
    setBusy(true);
    try {
      const { data: fresh } = await dataProvider.getOne<Policy>(ResourceName.POLICIES, { id: policy.id });
      // Result is handled via onSuccess/onError, not the call site (ADR-040).
      void update(
        ResourceName.POLICIES,
        { id: fresh.id, data: { ...fresh, is_accel_funding_enabled: value }, previousData: fresh },
        {
          onSuccess: () => {
            notify("Accel funding updated", { type: "success" });
            refresh();
            setBusy(false);
          },
          onError: (error: unknown) => {
            notifyError(error);
            setBusy(false);
          },
        }
      );
    } catch (error) {
      // The re-read failed — don't fall back to the stale snapshot, that's the
      // exact write this guard exists to prevent.
      notifyError(error);
      setBusy(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          mb: 2,
          px: 2,
          py: 1.5,
          borderRadius: "8px",
          border: "1px solid",
          borderColor: (theme) => (enabled ? alpha(theme.palette.success.main, 0.5) : theme.palette.divider),
          bgcolor: (theme) => (enabled ? alpha(theme.palette.success.main, 0.08) : "transparent"),
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
          <BoltIcon fontSize="small" sx={{ color: accent, flexShrink: 0 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Accel Funding{" "}
              <Box component="span" sx={{ color: accent }}>
                {enabled ? "Enabled" : "Disabled"}
              </Box>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {enabled
                ? "Claims on this policy can be submitted to Aura for funding."
                : "Claims on this policy are not eligible for Aura funding."}
            </Typography>
          </Box>
        </Box>
        <Switch
          checked={enabled}
          disabled={!editable || busy}
          onChange={(e) => setPending(e.target.checked)}
          inputProps={{ "aria-label": "Accel funding enabled" }}
        />
      </Box>
      <ConfirmationDialog
        open={pending !== null}
        title={pending ? "Enable Accel funding for this policy?" : "Disable Accel funding for this policy?"}
        message={
          pending
            ? "By enabling Accel funding you attest that this policy has a signed funding contract with Aegle. " +
              "Claims on this policy will become eligible for submission to Aura for funding. " +
              "Aegle will separately confirm the signed contract in house before the first funding request on this policy is approved."
            : "Claims on this policy will no longer be eligible for submission to Aura for funding. Claims already submitted are unaffected."
        }
        confirmLabel={pending ? ConfirmLabelType.Confirm : ConfirmLabelType.Continue}
        onConfirm={() => {
          const value = pending;
          setPending(null);
          if (value !== null) void setEnabled(value);
        }}
        onCancel={() => setPending(null)}
      />
    </>
  );
};
